/**
 * ProgressHeatmap - Visualizacion de consistencia estilo GitHub
 *
 * Muestra la consistencia de completado a lo largo del tiempo.
 * Diseñado para motivar sin juzgar:
 * - Colores suaves, no rojos punitivos
 * - Enfocado en patrones, no en perfeccion
 * - Tooltip con contexto del dia
 */

import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/core/utils';
import { useHistoryStore } from '@/store';
import type { DaySnapshot } from '@/core/types';
import { Tooltip } from '@/ui/primitives';

interface HeatmapDay {
  date: string;
  dayOfWeek: number;
  completionRate: number;
  hasData: boolean;
}

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const DAYS = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

// Generar los ultimos N dias
function generateDays(count: number): { date: string; dayOfWeek: number }[] {
  const days: { date: string; dayOfWeek: number }[] = [];
  const today = new Date();

  for (let i = count - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    days.push({
      date: date.toISOString().split('T')[0] ?? '',
      dayOfWeek: date.getDay(),
    });
  }

  return days;
}

// Obtener color segun porcentaje de completado
function getColorClass(rate: number, hasData: boolean): string {
  if (!hasData) return 'bg-slate-800/50';
  if (rate === 0) return 'bg-slate-800';
  if (rate < 25) return 'bg-emerald-900/40';
  if (rate < 50) return 'bg-emerald-800/60';
  if (rate < 75) return 'bg-emerald-700/70';
  if (rate < 100) return 'bg-emerald-600/80';
  return 'bg-emerald-500';
}

interface ProgressHeatmapProps {
  days?: number; // Cantidad de dias a mostrar (default 90)
}

export function ProgressHeatmap({ days = 90 }: ProgressHeatmapProps) {
  const { getDaySnapshots } = useHistoryStore();
  const [snapshots, setSnapshots] = useState<DaySnapshot[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Generar estructura de dias
  const dayStructure = useMemo(() => generateDays(days), [days]);

  // Calcular fechas inicio y fin
  const dateRange = useMemo(() => {
    const endDate = new Date().toISOString().split('T')[0] ?? '';
    const startDate = new Date(Date.now() - (days - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? '';
    return { startDate, endDate };
  }, [days]);

  // Cargar snapshots
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const data = await getDaySnapshots(dateRange.startDate, dateRange.endDate);
      setSnapshots(data);
      setIsLoading(false);
    };
    load();
  }, [getDaySnapshots, dateRange]);

  // Mapear snapshots a dias
  const heatmapData: HeatmapDay[] = useMemo(() => {
    const snapshotMap = new Map(snapshots.map((s) => [s.date, s]));

    return dayStructure.map((day) => {
      const snapshot = snapshotMap.get(day.date);
      return {
        date: day.date,
        dayOfWeek: day.dayOfWeek,
        completionRate: snapshot?.completionRate ?? 0,
        hasData: !!snapshot,
      };
    });
  }, [dayStructure, snapshots]);

  // Agrupar por semanas para el grid
  const weeks = useMemo(() => {
    const result: HeatmapDay[][] = [];
    let currentWeek: HeatmapDay[] = [];

    heatmapData.forEach((day, index) => {
      // Agregar dias vacios al inicio si la primera semana no empieza en domingo
      if (index === 0 && day.dayOfWeek > 0) {
        for (let i = 0; i < day.dayOfWeek; i++) {
          currentWeek.push({
            date: '',
            dayOfWeek: i,
            completionRate: 0,
            hasData: false,
          });
        }
      }

      currentWeek.push(day);

      // Nueva semana despues de sabado
      if (day.dayOfWeek === 6 || index === heatmapData.length - 1) {
        result.push(currentWeek);
        currentWeek = [];
      }
    });

    return result;
  }, [heatmapData]);

  // Obtener meses para labels
  const monthLabels = useMemo(() => {
    const labels: { month: string; weekIndex: number }[] = [];
    let lastMonth = -1;

    weeks.forEach((week, weekIndex) => {
      const firstDay = week.find((d) => d.date);
      if (firstDay?.date) {
        const month = new Date(firstDay.date).getMonth();
        if (month !== lastMonth) {
          labels.push({ month: MONTHS[month] ?? '', weekIndex });
          lastMonth = month;
        }
      }
    });

    return labels;
  }, [weeks]);

  // Calcular stats
  const stats = useMemo(() => {
    const withData = heatmapData.filter((d) => d.hasData);
    if (withData.length === 0) {
      return { activeDays: 0, avgRate: 0, perfectDays: 0 };
    }

    const avgRate = Math.round(
      withData.reduce((sum, d) => sum + d.completionRate, 0) / withData.length
    );
    const perfectDays = withData.filter((d) => d.completionRate === 100).length;

    return {
      activeDays: withData.length,
      avgRate,
      perfectDays,
    };
  }, [heatmapData]);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 w-24 bg-slate-800 rounded" />
        <div className="h-32 bg-slate-800/50 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header con stats */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-300">
          Actividad de los ultimos {days} dias
        </h3>
        <div className="flex gap-4 text-xs text-slate-500">
          <span>{stats.activeDays} dias activos</span>
          <span>{stats.avgRate}% promedio</span>
          <span>{stats.perfectDays} dias perfectos</span>
        </div>
      </div>

      {/* Heatmap grid */}
      <div className="overflow-x-auto pb-2">
        <div className="min-w-max">
          {/* Month labels */}
          <div className="flex mb-1 ml-6">
            {monthLabels.map(({ month, weekIndex }) => (
              <span
                key={`${month}-${weekIndex}`}
                className="text-2xs text-slate-600"
                style={{ marginLeft: weekIndex === 0 ? 0 : `${(weekIndex - (monthLabels[monthLabels.indexOf({ month, weekIndex }) - 1]?.weekIndex ?? 0)) * 14}px` }}
              >
                {month}
              </span>
            ))}
          </div>

          <div className="flex gap-0.5">
            {/* Day labels */}
            <div className="flex flex-col gap-0.5 mr-1">
              {DAYS.map((day, i) => (
                <div
                  key={day}
                  className={cn(
                    'w-4 h-3 text-2xs text-slate-600 flex items-center justify-center',
                    i % 2 === 1 ? 'opacity-0' : ''
                  )}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Weeks grid */}
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-0.5">
                {week.map((day, dayIndex) => (
                  <Tooltip
                    key={`${weekIndex}-${dayIndex}`}
                    content={
                      day.date
                        ? `${formatDate(day.date)}: ${day.hasData ? `${day.completionRate}%` : 'Sin datos'}`
                        : ''
                    }
                    position="top"
                    delay={200}
                  >
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: weekIndex * 0.02 }}
                      className={cn(
                        'w-3 h-3 rounded-sm',
                        day.date ? getColorClass(day.completionRate, day.hasData) : 'bg-transparent',
                        day.date && 'cursor-pointer hover:ring-1 hover:ring-slate-500'
                      )}
                    />
                  </Tooltip>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-2 text-2xs text-slate-600">
        <span>Menos</span>
        <div className="flex gap-0.5">
          <div className="w-3 h-3 rounded-sm bg-slate-800" />
          <div className="w-3 h-3 rounded-sm bg-emerald-900/40" />
          <div className="w-3 h-3 rounded-sm bg-emerald-800/60" />
          <div className="w-3 h-3 rounded-sm bg-emerald-700/70" />
          <div className="w-3 h-3 rounded-sm bg-emerald-500" />
        </div>
        <span>Mas</span>
      </div>
    </div>
  );
}

// Helper para formatear fecha
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  return `${day} ${month}`;
}
